import { useState, useEffect, useCallback } from 'react';
import {
  fetchStampPresets,
  createStampPreset,
  updateStampPreset,
  reorderStampPresets,
  deleteStampPreset,
  type StampPresetItem,
} from '../services/api';

export function useStampPresets() {
  const [presets, setPresets] = useState<StampPresetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** 從 API 載入所有印章 */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStampPresets();
      setPresets(data);
    } catch (e: any) {
      setError(e.message || '載入印章失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** 新增自訂印章 */
  const create = useCallback(async (name: string, file: File) => {
    const newPreset = await createStampPreset(name, file);
    setPresets(prev => [...prev, newPreset]);
    return newPreset;
  }, []);

  /** 切換啟用/停用 */
  const toggle = useCallback(async (id: string, enabled: boolean) => {
    // 先樂觀更新 UI
    setPresets(prev => prev.map(p => p.id === id ? { ...p, enabled } : p));
    try {
      await updateStampPreset(id, { enabled });
    } catch {
      // 還原
      setPresets(prev => prev.map(p => p.id === id ? { ...p, enabled: !enabled } : p));
    }
  }, []);

  /** 更新名稱 */
  const rename = useCallback(async (id: string, name: string) => {
    await updateStampPreset(id, { name });
    setPresets(prev => prev.map(p => p.id === id ? { ...p, name } : p));
  }, []);

  /** 拖曳排序後批次更新 */
  const reorder = useCallback(async (reorderedPresets: StampPresetItem[]) => {
    const withOrder = reorderedPresets.map((p, i) => ({ ...p, sortOrder: i }));
    setPresets(withOrder);

    const orders = withOrder.map(p => ({ id: p.id, sortOrder: p.sortOrder }));
    try {
      await reorderStampPresets(orders);
    } catch {
      // 失敗時重新載入
      await load();
    }
  }, [load]);

  /** 刪除自訂印章 */
  const remove = useCallback(async (id: string) => {
    await deleteStampPreset(id);
    setPresets(prev => prev.filter(p => p.id !== id));
  }, []);

  /** 篩出啟用中的印章（供 StampToolbar 使用） */
  const enabledPresets = presets
    .filter(p => p.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return {
    presets,
    enabledPresets,
    loading,
    error,
    load,
    create,
    toggle,
    rename,
    reorder,
    remove,
  };
}
